import { Component, inject, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { TableFooterComponent } from '../../../../_shared/components/table-footer/table-footer.component';
import { CostTrackingService } from '../../services/cost-tracking.service';
import { FilterService } from '../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { AssetCostItem } from '../../models/cost-tracking-response.model';
import { AssetCostTableRequest } from '../../models/cost-tracking-request.model';
import { WidgetFilterData } from '../../../../_shared/models/filters/widget-filter-data.model';
import { AuthService } from '../../../../_shared/services/auth.service';
import { formatCurrencyAmount, formatCurrencyPerKm } from '../../../../_shared/helpers/currency/currency-format.helper';
import { LookupLabelPipe } from '../../../../_shared/pipes/lookup-label.pipe';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../_shared/helpers/widget-filter.helper';

@Component({
  selector: 'app-asset-cost-table',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, TableFooterComponent, TranslocoDirective, TranslocoPipe, LookupLabelPipe],
  templateUrl: './asset-cost-table.component.html',
  styleUrl: './asset-cost-table.component.scss'
})
export class AssetCostTableComponent implements OnInit, OnChanges, OnDestroy {
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private transloco: TranslocoService = inject(TranslocoService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  isLoading: boolean = false;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';

  assetCosts: AssetCostItem[] = [];
  page: number = 1;
  @Input() pageSize: number = 4;

  @Input() searchTerm: string = '';
  totalPages: number = 0;
  totalCount: number = 0;

  private filterSubscription: Subscription;

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(async (filter) => {
      this.widgetFilter = filter;
      this.page = 1;
      await this.loadData();
    });
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  async ngOnInit(): Promise<void> {
    this.widgetFilter = this.filterService.filter;
    await this.loadData();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange && this.widgetFilter) {
      this.page = 1;
      await this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AssetCostTableRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm?.trim() || undefined
      };

      const response = await this.costTrackingService.getAssetCostTable(request, this.cancelSubject);

      this.assetCosts = response.data?.results ?? [];
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  goToPage(page: number): void {
    this.page = page;
    this.loadData();
  }

  onExport(): void {
    console.log('Export clicked');
  }

  formatCost(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return this.transloco.translate('costTracking.table.notAvailable');
    }

    return formatCurrencyAmount(this.currencySymbol, value);
  }

  formatCostPerKm(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return this.transloco.translate('costTracking.table.notAvailable');
    }

    return formatCurrencyPerKm(this.currencySymbol, value);
  }
}